<template>
  <div>
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-layout wrap justify-center>
      <v-flex xs12>
        <v-layout wrap justify-start pa-5>
          <!-- 1. PAGE HEADING -->
          <v-flex xs12>
            <div class="op-title">Operation Details</div>
            <div class="op-subtitle">View and manage all snake operation reports</div>
          </v-flex>

          <!-- 2. STATUS SUMMARY BOXES -->
          <v-flex xs12 pt-4>
            <v-layout wrap justify-start class="mx-n2">
              <v-flex v-for="(box, i) in statusBoxes" :key="i" xs6 sm4 md3 class="seven-cols-lg pa-2">
                <div class="op-stat" :style="cardStyle(box)" @click="selectStatus(box.status)">
                  <div class="op-stat__icon" :style="iconStyle(box)">
                    <v-icon size="20" :color="isActive(box) ? '#FFFFFF' : box.accent">{{ box.icon }}</v-icon>
                  </div>
                  <div class="op-stat__body">
                    <div class="op-stat__count" :style="{ color: isActive(box) ? '#FFFFFF' : box.accent }">
                      {{ stats[box.key] || 0 }}
                    </div>
                    <div class="op-stat__label" :style="{ color: isActive(box) ? 'rgba(255,255,255,0.78)' : '#6B7C72' }">
                      {{ box.name }}
                    </div>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- 3. FILTERS + TABLE CARD -->
          <v-flex xs12 pt-4>
            <div class="op-card">
              <!-- TOOLBAR -->
              <div class="op-toolbar">
                <button type="button" class="op-allbtn" :class="{ 'op-allbtn--off': hasFilters }" @click="resetAll()">
                  <v-icon size="16" color="#FFFFFF">mdi-view-grid-outline</v-icon>
                  <span>All</span>
                </button>

                <!-- Time period -->
                <v-menu v-model="menuTime" :close-on-content-click="false" offset-y bottom
                  transition="slide-y-transition" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="op-pill" :class="{ 'op-pill--on': hasDateFilter }" v-bind="attrs"
                      v-on="on">
                      <v-icon size="16" :color="hasDateFilter ? '#427D2D' : '#7A8B80'">mdi-calendar-blank-outline</v-icon>
                      <span class="op-pill__txt">{{ timeLabel }}</span>
                      <v-icon size="18" color="#9AA8A0">mdi-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-card class="op-menu" flat>
                    <div class="op-menu__title">Select a date range</div>
                    <v-date-picker v-model="dateRange" range no-title color="#427D2D" :max="maxDate" min="1950-01-01"
                      class="op-datepicker"></v-date-picker>
                    <div class="op-menu__actions">
                      <button type="button" class="op-mini" @click="clearTime()">Clear</button>
                      <button type="button" class="op-mini op-mini--primary" @click="applyTime()">Apply</button>
                    </div>
                  </v-card>
                </v-menu>

                <!-- Snakes -->
                <v-menu v-model="menuSnake" :close-on-content-click="false" offset-y bottom
                  transition="slide-y-transition" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="op-pill" :class="{ 'op-pill--on': allsnakelist.length > 0 }"
                      v-bind="attrs" v-on="on">
                      <v-icon size="16" :color="allsnakelist.length ? '#427D2D' : '#7A8B80'">mdi-snake</v-icon>
                      <span class="op-pill__txt">{{ snakeLabel }}</span>
                      <v-icon size="18" color="#9AA8A0">mdi-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-card class="op-menu" flat>
                    <div class="op-menu__title">Select snakes</div>
                    <div class="op-menu__list">
                      <div v-for="snake in snakeitems" :key="snake._id" class="op-opt" @click="toggleSnake(snake._id)">
                        <v-icon size="18" :color="allsnakelist.indexOf(snake._id) > -1 ? '#427D2D' : '#BFC9C2'">
                          {{ allsnakelist.indexOf(snake._id) > -1 ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                        </v-icon>
                        <span>{{ snake.name }}</span>
                      </div>
                      <div v-if="!snakeitems || !snakeitems.length" class="op-opt op-opt--empty">No snakes available</div>
                    </div>
                    <div class="op-menu__actions">
                      <button type="button" class="op-mini" @click="clearSnakes()">Clear</button>
                      <button type="button" class="op-mini op-mini--primary" @click="applySnakes()">Apply</button>
                    </div>
                  </v-card>
                </v-menu>

                <!-- District -->
                <v-menu v-model="menuDistrict" :close-on-content-click="false" offset-y bottom
                  transition="slide-y-transition" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="op-pill" :class="{ 'op-pill--on': !!bigsnakeselect }" v-bind="attrs"
                      v-on="on">
                      <v-icon size="16" :color="bigsnakeselect ? '#427D2D' : '#7A8B80'">mdi-map-marker-outline</v-icon>
                      <span class="op-pill__txt">{{ districtLabel }}</span>
                      <v-icon size="18" color="#9AA8A0">mdi-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-card class="op-menu" flat>
                    <div class="op-menu__title">Select district</div>
                    <div class="op-menu__list">
                      <div v-for="district in districts" :key="district" class="op-opt" @click="pickDistrict(district)">
                        <v-icon size="18" :color="bigsnakeselect === district ? '#427D2D' : '#BFC9C2'">
                          {{ bigsnakeselect === district ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                        </v-icon>
                        <span>{{ district }}</span>
                      </div>
                    </div>
                  </v-card>
                </v-menu>

                <v-spacer></v-spacer>

                <!-- Search -->
                <v-text-field v-model="search" class="op-search" placeholder="Search operations..."
                  prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                  @input="onSearch"></v-text-field>

                <!-- More filters -->
                <button type="button" class="op-iconbtn" :class="{ 'op-iconbtn--on': showAdvanced }"
                  title="More filters" @click="showAdvanced = !showAdvanced">
                  <v-icon size="18" color="#427D2D">mdi-filter-variant</v-icon>
                </button>

                <!-- Export -->
                <button type="button" class="op-iconbtn" title="Download this page as CSV" @click="exportCsv()">
                  <v-icon size="18" color="#5A6B60">mdi-download-outline</v-icon>
                </button>
              </div>

              <!-- ADVANCED FILTERS -->
              <v-expand-transition>
                <div v-if="showAdvanced" class="op-advanced">
                  <v-layout wrap class="mx-n2">
                    <v-flex xs12 sm4 md3 class="px-2">
                      <div class="op-flabel">Situation</div>
                      <v-select :items="situationlist" v-model="selecteduser" placeholder="All situations" outlined dense
                        hide-details clearable class="op-select"></v-select>
                    </v-flex>
                    <v-flex xs12 sm4 md3 class="px-2">
                      <div class="op-flabel">Status</div>
                      <v-select :items="casestatuslist" v-model="casestatusdata" placeholder="All statuses" outlined
                        dense hide-details clearable class="op-select"></v-select>
                    </v-flex>
                    <v-flex xs12 sm4 md2 class="px-2">
                      <div class="op-flabel">Rows per page</div>
                      <v-select :items="countList" v-model="count" outlined dense hide-details
                        class="op-select"></v-select>
                    </v-flex>
                    <v-flex xs12 md4 class="px-2 op-advanced__actions">
                      <button type="button" class="op-mini op-mini--primary" @click="applyAdvanced()">Apply</button>
                      <button type="button" class="op-mini" @click="resetAll()">Reset</button>
                    </v-flex>
                  </v-layout>
                </div>
              </v-expand-transition>

              <!-- TABLE -->
              <div class="op-table-wrap" v-if="items && items.length > 0">
                <v-simple-table class="op-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left op-th" @click="toggleSort('date')">
                          <span>Reported Date</span>
                          <v-icon size="13" :color="sortColor('date')">{{ sortIcon('date') }}</v-icon>
                        </th>
                        <th class="text-left op-th" @click="toggleSort('district')">
                          <span>District</span>
                          <v-icon size="13" :color="sortColor('district')">{{ sortIcon('district') }}</v-icon>
                        </th>
                        <th class="text-left op-th op-th--plain">Description</th>
                        <th class="text-left op-th" @click="toggleSort('situation')">
                          <span>Situation</span>
                          <v-icon size="13" :color="sortColor('situation')">{{ sortIcon('situation') }}</v-icon>
                        </th>
                        <th class="text-left op-th" @click="toggleSort('snake')">
                          <span>Snake</span>
                          <v-icon size="13" :color="sortColor('snake')">{{ sortIcon('snake') }}</v-icon>
                        </th>
                        <th class="text-left op-th" @click="toggleSort('status')">
                          <span>Status</span>
                          <v-icon size="13" :color="sortColor('status')">{{ sortIcon('status') }}</v-icon>
                        </th>
                        <th class="text-left op-th op-th--plain">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in sortedItems" :key="index">
                        <!-- Reported date -->
                        <td>
                          <v-icon size="15" color="#8A9990" class="op-cellicon">mdi-calendar-blank-outline</v-icon>
                          <span>{{ formatDate(item.reportedAt) }}</span>
                        </td>

                        <!-- District -->
                        <td>
                          <span v-if="reportValue(item, 'district')">{{ reportValue(item, 'district') }}</span>
                          <span v-else class="op-dash">-------</span>
                        </td>

                        <!-- Description -->
                        <td class="op-desc" @click="openModal(reportValue(item, 'description'))">
                          <span v-if="reportValue(item, 'description')">
                            {{ reportValue(item, 'description').slice(0, 30) }}..
                          </span>
                          <span v-else class="op-dash">-------</span>
                        </td>

                        <!-- Situation -->
                        <td>
                          <span v-if="reportValue(item, 'situation')" class="op-chip"
                            :style="situationStyle(reportValue(item, 'situation'))">
                            {{ reportValue(item, 'situation') }}
                          </span>
                          <span v-else class="op-dash">-------</span>
                        </td>

                        <!-- Snake -->
                        <td>
                          <span v-if="snakeName(item)">{{ snakeName(item) }}</span>
                          <span v-else class="op-dash">-------</span>
                        </td>

                        <!-- Status -->
                        <td>
                          <span class="op-chip" :style="statusStyle(item.status)">
                            <v-icon size="13" :color="statusColors(item.status).fg">{{ statusIcon(item.status) }}</v-icon>
                            {{ item.status }}
                          </span>
                        </td>

                        <!-- Action -->
                        <td>
                          <v-icon small color="#2E9E52" class="op-act mr-2" title="View Details"
                            @click="openviewdetails(item)">
                            mdi-eye-outline
                          </v-icon>
                          <template v-if="mainRole === 'admin'">
                            <v-icon v-if="item.status === 'Released'" small color="#1D7AF3" class="op-act mr-2"
                              title="Approve Operation" @click="triggerApprove(item)">
                              mdi-check-circle-outline
                            </v-icon>
                            <v-icon v-if="['Pending', 'Assigned', 'Rescued', 'Released'].includes(item.status)" small
                              color="#EF9400" class="op-act mr-2" title="Close Operation" @click="triggerClose(item)">
                              mdi-close-circle-outline
                            </v-icon>
                            <v-icon v-if="item.status !== 'Fraud'" small color="#E5484D" class="op-act"
                              title="Delete Operation" @click="triggerDelete(item)">
                              mdi-delete-outline
                            </v-icon>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <!-- EMPTY STATE -->
              <div class="op-empty" v-else>
                <v-icon size="42" color="#CBD5CE">mdi-file-search-outline</v-icon>
                <div class="op-empty__title">No Data Found!</div>
                <div class="op-empty__sub">Try changing the filters or the search keyword</div>
              </div>

              <!-- PAGINATION -->
              <div class="op-footer" v-if="items && items.length > 0 && Pagelength > 1">
                <!-- <span class="op-footer__txt">Page {{ currentPage }} of {{ Pagelength }}</span> -->
                <v-pagination v-model="currentPage" :length="Pagelength" :total-visible="7" circle color="#427D2D"
                  small></v-pagination>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- delete box dialog-->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Confirmation</v-card-title>
        <v-card-text>Are you sure you want to delete?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="deleteDialog = false"
            class="itemValue">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="deleteoperation()" class="itemValue">
            <span style="color: #fff">Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CLOSE OPERATION DIALOG -->
    <v-dialog v-model="closeOperationDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Close Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12 class="subheading">
                  <span>Remarks</span>
                  <v-text-field outlined dense v-model="closeremarks"></v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="closeOperationDialog = false">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="closeoperation()">
            <span style="color: #fff">Close</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- APPROVE OPERATION DIALOG -->
    <v-dialog v-model="approveOperationDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Approve Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <v-layout wrap justify-start>
                <v-flex xs12 class="subheading">
                  <span>Remarks</span>
                  <v-text-field outlined dense v-model="approveremarks"></v-text-field>
                </v-flex>
                <v-flex xs12 class="subheading">
                  <span>ID by Admin</span>
                  <v-text-field outlined dense v-model="idByAdmin"></v-text-field>
                </v-flex>
                <v-flex xs12 class="subheading">
                  <span>Distance (in km)</span>
                  <v-text-field outlined dense v-model="distance"></v-text-field>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="approveOperationDialog = false">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="approveoperation()">
            <span style="color: #fff">Approve</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Confirmation</v-card-title>
        <v-card-text>Are you sure you want to reject?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" light :ripple="false" depressed @click="rejectDialog = false"
            class="itemValue">
            Cancel
          </v-btn>
          <v-btn tile :color="appColor" light :ripple="false" depressed @click="rejectoperation()" class="itemValue">
            <span style="color: #fff">Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- description -->
    <v-dialog v-model="isModalOpen" max-width="500px">
      <v-card>
        <v-card-title class="tablehead">Full Description</v-card-title>
        <v-card-text class="label" style="color: black;">
          {{ fullDescription || 'No description available' }}
        </v-card-text>
        <v-card-actions>
          <v-btn color="#FF1313" text @click="closeModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from "axios";
export default {
  data() {
    return {
      //status of cases
      stats: {
        pendingCount: 0,
        assignedCount: 0,
        rescuedCount: 0,
        releasedCount: 0,
        approvedCount: 0,
        fraudCount: 0,
        totalCount: 0
      },
      statusBoxes: [
        {
          name: 'Total Cases',
          icon: 'mdi-file-document-multiple-outline',
          accent: '#37474F',
          bg: '#F2F5F4',
          border: '#E2E8E5',
          iconBg: '#E4EAE8',
          activeBg: '#1E3B33',
          route: 'snakereports',
          key: 'totalCount',
          status: ''
        },
        {
          name: 'Pending',
          icon: 'mdi-clock-outline',
          accent: '#E39A0C',
          bg: '#FFF8EA',
          border: '#F7E3BC',
          iconBg: '#FCEBCE',
          activeBg: '#B87708',
          route: 'pendingoperations',
          key: 'pendingCount',
          status: 'Pending'
        },
        {
          name: 'Assigned',
          icon: 'mdi-account-check-outline',
          accent: '#3B82F6',
          bg: '#EFF5FF',
          border: '#D6E4FB',
          iconBg: '#DFEAFD',
          activeBg: '#1D4ED8',
          route: 'assignedoperations',
          key: 'assignedCount',
          status: 'Assigned'
        },
        {
          name: 'Rescued',
          icon: 'mdi-snake',
          accent: '#16A34A',
          bg: '#EBF7F0',
          border: '#CDE9DA',
          iconBg: '#D9F0E3',
          activeBg: '#15803D',
          route: 'rescuedoperations',
          key: 'rescuedCount',
          status: 'Rescued'
        },
        {
          name: 'Released',
          icon: 'mdi-map-marker-check-outline',
          accent: '#0D9488',
          bg: '#E9F7F6',
          border: '#C8E8E6',
          iconBg: '#D6EFED',
          activeBg: '#0F766E',
          route: 'releasedoperations',
          key: 'releasedCount',
          status: 'Released'
        },
        {
          name: 'Approved',
          icon: 'mdi-shield-check-outline',
          accent: '#65A30D',
          bg: '#F2F9E9',
          border: '#DDEDC7',
          iconBg: '#E7F3D6',
          activeBg: '#4D7C0F',
          route: 'approvedoperations',
          key: 'approvedCount',
          status: 'Approved'
        },
        {
          name: 'Fraud Case',
          icon: 'mdi-alert-circle-outline',
          accent: '#E5484D',
          bg: '#FEF1F1',
          border: '#FBD7D8',
          iconBg: '#FDE0E1',
          activeBg: '#B91C1C',
          route: 'fraudcases',
          key: 'fraudCount',
          status: 'Fraud'
        },
      ],
      isModalOpen: false,
      fullDescription: '',
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      count: 20,
      countList: [10, 20, 50, 100],
      keyword: "",
      search: "",
      searchTimer: null,
      currentPage: 1,
      Pagelength: 0,
      userlist: [],
      items: [],
      snakeitems: [],
      status: "",
      deleteDialog: false,
      deleteid: "",
      rejectDialog: false,
      rejectid: "",
      districts: [
        "All",
        "Angul",
        "Boudh",
        "Balangir",
        "Bargarh",
        "Balasore",
        "Bhadrak",
        "Cuttack",
        "Deogarh",
        "Dhenkanal",
        "Ganjam",
        "Gajapati",
        "Jharsuguda",
        "Jajpur",
        "Jagatsinghapur",
        "Khordha",
        "Keonjhar",
        "Kalahandi",
        "Kandhamal",
        "Koraput",
        "Kendrapara",
        "Malkangiri",
        "Mayurbhanj",
        "Nabarangpur",
        "Nuapada",
        "Nayagarh",
        "Puri",
        "Rayagada",
        "Sambalpur",
        "Subarnapur",
        "Sundargarh",
      ],
      // filter dropdown state
      menuTime: false,
      menuSnake: false,
      menuDistrict: false,
      showAdvanced: false,
      dateRange: [],
      fromdate: null,
      todate: null,
      bigsnakeselect: "",
      allsnakelist: [],
      selecteduser: "",
      situationlist: [
        "Inside the house",
        "Just outside the building",
        "Natural"
      ],
      casestatusdata: "",
      casestatuslist: [
        "Pending",
        "Assigned",
        "Approved",
        "Rescued",
        "Released",
        "Fraud",
      ],
      // table sorting (applies to the loaded page)
      sortKey: "",
      sortAsc: true,
      //adding options directly in listing page
      closeOperationDialog: false,
      closeOperationId: "",
      closeremarks: "",

      approveOperationDialog: false,
      approveOperationId: "",
      approveremarks: "",
      idByAdmin: "",
      distance: "",
    };
  },
  beforeMount() {
    this.getStats();//status of cases
    this.getData();
    this.getList();
  },
  watch: {
    currentPage() {
      this.getData();
    },
    count() {
      this.refresh();
    },
  },
  //adding options directly in listing page
  computed: {
    mainRole() {
      return this.$store.state.userRole;
    },
    maxDate() {
      return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 10);
    },
    hasDateFilter() {
      return !!(this.fromdate || this.todate);
    },
    hasFilters() {
      return !!(
        this.fromdate ||
        this.todate ||
        this.bigsnakeselect ||
        (this.allsnakelist && this.allsnakelist.length) ||
        this.selecteduser ||
        this.casestatusdata ||
        this.keyword
      );
    },
    timeLabel() {
      if (this.fromdate && this.todate)
        return this.formatDate(this.fromdate) + " - " + this.formatDate(this.todate);
      if (this.fromdate) return "From " + this.formatDate(this.fromdate);
      if (this.todate) return "Up to " + this.formatDate(this.todate);
      return "Time Period";
    },
    snakeLabel() {
      if (!this.allsnakelist || !this.allsnakelist.length) return "All Snakes";
      if (this.allsnakelist.length === 1) {
        const match = this.snakeitems.find((s) => s._id === this.allsnakelist[0]);
        return match ? match.name : "1 Snake";
      }
      return this.allsnakelist.length + " Snakes";
    },
    districtLabel() {
      return this.bigsnakeselect ? this.bigsnakeselect : "District";
    },
    sortedItems() {
      const list = (this.items || []).slice();
      if (!this.sortKey) return list;
      const direction = this.sortAsc ? 1 : -1;
      const value = (item) => {
        switch (this.sortKey) {
          case "date":
            return item.reportedAt || "";
          case "district":
            return this.reportValue(item, "district");
          case "situation":
            return this.reportValue(item, "situation");
          case "snake":
            return this.snakeName(item);
          case "status":
            return item.status || "";
          default:
            return "";
        }
      };
      return list.sort((a, b) => {
        const x = String(value(a)).toLowerCase();
        const y = String(value(b)).toLowerCase();
        if (x === y) return 0;
        return (x < y ? -1 : 1) * direction;
      });
    },
  },
  methods: {
    // ---------- status cards ----------
    isActive(box) {
      return this.casestatusdata === box.status;
    },
    cardStyle(box) {
      if (this.isActive(box)) {
        return {
          background: box.activeBg,
          border: "1px solid " + box.activeBg,
          boxShadow: "0 8px 18px rgba(30, 59, 51, 0.18)",
        };
      }
      return { background: box.bg, border: "1px solid " + box.border };
    },
    iconStyle(box) {
      return {
        background: this.isActive(box) ? "rgba(255, 255, 255, 0.18)" : box.iconBg,
      };
    },
    selectStatus(status) {
      this.casestatusdata = status;
      this.refresh();
    },

    // ---------- filters ----------
    refresh() {
      // the currentPage watcher already reloads, so avoid firing twice
      if (this.currentPage !== 1) this.currentPage = 1;
      else this.getData();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.keyword = this.search || "";
        this.refresh();
      }, 450);
    },
    applyTime() {
      const range = (this.dateRange || []).slice().sort();
      this.fromdate = range[0] || null;
      this.todate = range[1] || range[0] || null;
      this.menuTime = false;
      this.refresh();
    },
    clearTime() {
      this.dateRange = [];
      this.fromdate = null;
      this.todate = null;
      this.menuTime = false;
      this.refresh();
    },
    toggleSnake(id) {
      const position = this.allsnakelist.indexOf(id);
      if (position > -1) this.allsnakelist.splice(position, 1);
      else this.allsnakelist.push(id);
    },
    applySnakes() {
      this.menuSnake = false;
      this.refresh();
    },
    clearSnakes() {
      this.allsnakelist = [];
      this.menuSnake = false;
      this.refresh();
    },
    pickDistrict(district) {
      // "All" behaves as no district filter
      this.bigsnakeselect =
        district === "All" || district === this.bigsnakeselect ? "" : district;
      this.menuDistrict = false;
      this.refresh();
    },
    applyAdvanced() {
      this.refresh();
    },
    resetAll() {
      this.dateRange = [];
      this.fromdate = null;
      this.todate = null;
      this.bigsnakeselect = "";
      this.allsnakelist = [];
      this.selecteduser = "";
      this.casestatusdata = "";
      this.search = "";
      this.keyword = "";
      this.sortKey = "";
      this.sortAsc = true;
      this.refresh();
    },

    // ---------- table helpers ----------
    toggleSort(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return "mdi-unfold-more-horizontal";
      return this.sortAsc ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    sortColor(key) {
      // sits on the green header, so both states are white
      return this.sortKey === key ? "#FFFFFF" : "rgba(255, 255, 255, 0.6)";
    },
    reportValue(item, field) {
      return (item && item.reportId && item.reportId[field]) || "";
    },
    snakeName(item) {
      // rescue.snake is the species identified during the rescue and may differ from
      // the one guessed by the reporter, so it wins for Rescued/Released/Approved.
      // Pending/Assigned cases only ever have the reported species.
      const identified =
        item && item.rescue && item.rescue.snake && item.rescue.snake.name;
      const reported =
        item && item.reportId && item.reportId.snake && item.reportId.snake.name;
      return identified || reported || "";
    },
    situationStyle(situation) {
      const map = {
        "Inside the house": { bg: "#EAF2FE", fg: "#2563EB" },
        "Just outside the building": { bg: "#F1EEFC", fg: "#6D5BD0" },
        "Natural habitat": { bg: "#E9F6EE", fg: "#16A34A" },
        Natural: { bg: "#E9F6EE", fg: "#16A34A" },
      };
      const tone = map[situation] || { bg: "#EEF2F0", fg: "#4A5A50" };
      return { background: tone.bg, color: tone.fg };
    },
    statusColors(status) {
      const map = {
        Pending: { bg: "#FFF6E5", fg: "#D98E04" },
        Assigned: { bg: "#EFF5FF", fg: "#2563EB" },
        Rescued: { bg: "#EBF7F0", fg: "#16A34A" },
        Released: { bg: "#E9F7F6", fg: "#0D9488" },
        Approved: { bg: "#F2F9E9", fg: "#65A30D" },
        Fraud: { bg: "#FEF1F1", fg: "#E5484D" },
        Closed: { bg: "#F1F3F2", fg: "#5A6B60" },
      };
      return map[status] || { bg: "#F1F3F2", fg: "#5A6B60" };
    },
    statusStyle(status) {
      const tone = this.statusColors(status);
      return { background: tone.bg, color: tone.fg };
    },
    statusIcon(status) {
      const map = {
        Pending: "mdi-clock-outline",
        Assigned: "mdi-account-check-outline",
        Rescued: "mdi-snake",
        Released: "mdi-map-marker-check-outline",
        Approved: "mdi-shield-check-outline",
        Fraud: "mdi-alert-circle-outline",
        Closed: "mdi-close-circle-outline",
      };
      return map[status] || "mdi-information-outline";
    },
    exportCsv() {
      if (!this.items || !this.items.length) {
        this.msg = "Nothing to download.";
        this.showSnackBar = true;
        return;
      }
      const rows = [
        ["Reported Date", "District", "Description", "Situation", "Snake", "Status"],
      ];
      this.sortedItems.forEach((item) => {
        rows.push([
          this.formatDate(item.reportedAt),
          this.reportValue(item, "district"),
          this.reportValue(item, "description"),
          this.reportValue(item, "situation"),
          this.snakeName(item),
          item.status || "",
        ]);
      });
      const csv = rows
        .map((row) =>
          row
            .map((cell) => '"' + String(cell == null ? "" : cell).replace(/"/g, '""') + '"')
            .join(",")
        )
        .join("\n");
      // BOM keeps Odia/Unicode text readable when the CSV is opened in Excel
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "operation-details-page-" + this.currentPage + ".csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },

    //status of cases
    viewDetailedReport(item) {
      this.$router.push({
        path: item.path,
        query: {
          fromDate: this.fromdate,
          toDate: this.todate,
          snake: this.allsnakelist,
          district: this.bigsnakeselect
        }
      });
    },

    //adding options directly in listing page
    triggerClose(item) {
      this.closeOperationId = item._id;
      this.closeremarks = "";
      this.closeOperationDialog = true;
    },
    triggerApprove(item) {
      this.approveOperationId = item._id;
      this.approveremarks = "";
      this.idByAdmin = "";
      this.distance = "";
      this.approveOperationDialog = true;
    },
    triggerDelete(item) {
      this.deleteid = item._id;
      this.deleteDialog = true;
    },

    closeoperation() {
      if (!this.closeremarks) {
        this.msg = "Please enter remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/closereport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.closeOperationId,
          remarks: this.closeremarks,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Report Closed Successfully.";
            this.showSnackBar = true;
            this.closeOperationDialog = false;
            this.getData(); // Refresh table data
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    approveoperation() {
      if (!this.approveremarks) {
        this.msg = "Please enter remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/approve",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.approveOperationId,
          remarks: this.approveremarks,
          idByAdmin: this.idByAdmin,
          distance: this.distance,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Approved Successfully.";
            this.showSnackBar = true;
            this.approveOperationDialog = false;
            this.getData(); // Refresh table data
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    getStats() {
      axios({
        method: "GET",
        url: "/snake/report/listCount",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          fromDate: this.fromdate,
          toDate: this.todate,
          snake: this.allsnakelist,
          district: this.bigsnakeselect
        }
      }).then(response => {
        if (response.data.status) this.stats = response.data.data;
      });
    }
    ,
    openviewdetails(item) {
      // Determine base path depending on the current layout route
      let basePath = "/snakereportsview";
      if (this.$route.path.startsWith("/officers")) {
        basePath = "/officers/snakereportsview";
      } else if (this.$route.path.startsWith("/DistrictEmergencyOfficer")) {
        basePath = "/DistrictEmergencyOfficer/snakereportsview";
      } else if (this.$route.path.startsWith("/stateMedical")) {
        basePath = "/stateMedical/snakereportsview";
      } else if (this.$route.path.startsWith("/stateDisaster")) {
        basePath = "/stateDisaster/snakereportsview";
      }
      this.$router.push({
        path: basePath,
        query: {
          id: item._id,
        },
      });
    },
    openModal(description) {
      this.fullDescription = description || 'No description available'; // Set a default message if description is missing
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },

    openrejectDialog(item) {
      this.rejectid = item._id;
      this.rejectDialog = true;
    },
    getData() {
      this.getStats()
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/operation/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: this.count,
          page: this.currentPage,
          keyword: this.keyword,
          fromDate: this.fromdate,
          toDate: this.todate,
          snake: this.allsnakelist,
          district: this.bigsnakeselect,
          situation: this.selecteduser,
          status: this.casestatusdata,
        }
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.items = response.data.data;
              this.Pagelength = response.data.pages;
              this.appLoading = false;
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/snake/all/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.snakeitems = response.data.data;
              this.appLoading = false;
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    deleteoperation() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/delete",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.deleteid,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = "Deleted Successfully.";
              this.showSnackBar = true;
              this.appLoading = false;
              this.deleteDialog = false;
              this.getData();
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
            this.deleteDialog = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    rejectoperation() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/operation/removedeleterequest",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.rejectid,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            {
              this.msg = "Rejected Successfully.";
              this.showSnackBar = true;
              this.appLoading = false;
              this.rejectDialog = false;
            }
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
            this.rejectDialog = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    formatDate(date) {
      if (!date) return "-";
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },
  },
};
</script>
<style>
@media (min-width: 1264px) {
  .seven-cols-lg {
    flex: 0 0 14.285% !important;
    max-width: 14.285% !important;
  }
}

/* ---------- page heading ---------- */
.op-title {
  font-family: poppinsregular;
  font-size: 22px;
  font-weight: 900;
  color: #1F2A24;
  line-height: 1.2;
}

.op-subtitle {
  font-family: sofiaProRegular;
  font-size: 13px;
  color: #7A8B80;
  padding-top: 2px;
}

/* ---------- status cards ---------- */
.op-stat {
  display: flex;
  align-items: center;
  min-height: 84px;
  padding: 14px;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease;
}

.op-stat:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 22px rgba(31, 42, 36, 0.12);
}

.op-stat__icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  margin-right: 12px;
}

.op-stat__body {
  min-width: 0;
}

.op-stat__count {
  font-family: poppinsregular;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.05;
}

.op-stat__label {
  font-family: sofiaProSemiBold;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  padding-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- shell card ---------- */
.op-card {
  background: #FFFFFF;
  border: 1px solid #EDF1EE;
  border-radius: 18px;
  box-shadow: 0 4px 18px rgba(31, 42, 36, 0.05);
  overflow: hidden;
}

/* ---------- toolbar ---------- */
.op-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 14px 16px;
}

.op-toolbar > * {
  margin-right: 10px;
  margin-bottom: 6px;
  margin-top: 6px;
}

.op-toolbar > .spacer {
  margin: 0;
}

.op-allbtn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: #4CAF50;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.28);
  color: #FFFFFF;
  font-family: sofiaProSemiBold;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s ease;
}

.op-allbtn span {
  margin-left: 7px;
}

.op-allbtn--off {
  background: #8DBF8A;
  box-shadow: none;
}

.op-allbtn:hover {
  background: #43A047;
}

.op-pill {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #E4EAE6;
  border-radius: 10px;
  background: #FFFFFF;
  font-family: sofiaProRegular;
  font-size: 13px;
  color: #3C4A42;
  cursor: pointer;
  transition: 0.2s ease;
}

.op-pill:hover {
  border-color: #CBD8D0;
  background: #FAFCFA;
}

.op-pill--on {
  border-color: #427D2D;
  background: #F2F8EF;
  color: #2F5E20;
}

.op-pill__txt {
  margin: 0 8px;
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.op-iconbtn {
  width: 38px;
  height: 38px;
  border: 1px solid #E4EAE6;
  border-radius: 10px;
  background: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s ease;
}

.op-iconbtn:hover {
  background: #F6F9F5;
  border-color: #CBD8D0;
}

.op-iconbtn--on {
  background: #EDF6E8;
  border-color: #CBE3BE;
}

.op-search {
  max-width: 250px;
  flex: 0 1 250px;
}

.op-search .v-input__slot {
  border-radius: 10px !important;
  min-height: 38px !important;
}

.op-search fieldset {
  border-color: #E4EAE6 !important;
}

.op-search input::placeholder {
  color: #9AA8A0 !important;
  font-family: sofiaProRegular;
  font-size: 13px;
}

/* ---------- dropdown menus ---------- */
.op-menu {
  border-radius: 14px !important;
  padding: 10px 0 8px;
  border: 1px solid #EDF1EE !important;
}

.op-menu__title {
  font-family: sofiaProSemiBold;
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #7A8B80;
  padding: 4px 16px 8px;
}

.op-menu__list {
  max-height: 260px;
  overflow-y: auto;
  min-width: 230px;
}

.op-opt {
  display: flex;
  align-items: center;
  padding: 7px 16px;
  font-family: sofiaProRegular;
  font-size: 13.5px;
  color: #2F3A33;
  cursor: pointer;
}

.op-opt span {
  margin-left: 9px;
}

.op-opt:hover {
  background: #F6F9F5;
}

.op-opt--empty {
  color: #9AA8A0;
  cursor: default;
}

.op-menu__actions {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px 2px;
  border-top: 1px solid #F0F3F1;
  margin-top: 6px;
}

.op-datepicker {
  box-shadow: none !important;
}

.op-mini {
  height: 32px;
  padding: 0 14px;
  margin-left: 8px;
  border: 1px solid #E4EAE6;
  border-radius: 8px;
  background: #FFFFFF;
  font-family: sofiaProSemiBold;
  font-size: 12.5px;
  color: #5A6B60;
  cursor: pointer;
}

.op-mini:hover {
  background: #F6F9F5;
}

.op-mini--primary {
  background: #427D2D;
  border-color: #427D2D;
  color: #FFFFFF;
}

.op-mini--primary:hover {
  background: #386A26;
}

/* ---------- advanced filters ---------- */
.op-advanced {
  padding: 4px 16px 18px;
  border-bottom: 1px solid #F0F3F1;
}

.op-flabel {
  font-family: sofiaProSemiBold;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #7A8B80;
  padding-bottom: 5px;
}

.op-select .v-input__slot {
  border-radius: 10px !important;
}

.op-select fieldset {
  border-color: #E4EAE6 !important;
}

.op-advanced__actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding-top: 22px;
}

.op-advanced__actions .op-mini:first-child {
  margin-left: 0;
}

/* ---------- table ---------- */
.op-table-wrap {
  overflow-x: auto;
}

.op-table thead th {
  background: #059669 !important;
  height: 44px !important;
  border-bottom: none !important;
}

.op-table .op-th {
  font-family: sofiaProSemiBold;
  font-size: 11px !important;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #ffffff !important;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.op-table .op-th span {
  margin-right: 5px;
  vertical-align: middle;
}

.op-table .op-th--plain {
  cursor: default;
}

.op-table tbody td {
  height: 48px !important;
  border-bottom: 1px solid #F1F4F2 !important;
  font-family: sofiaProRegular;
  font-size: 13.5px !important;
  color: #2F3A33 !important;
  white-space: nowrap;
}

.op-table tbody tr:hover {
  background: #FAFCFA !important;
}

.op-table tbody tr:last-child td {
  border-bottom: none !important;
}

.op-cellicon {
  margin-right: 7px;
  vertical-align: text-bottom;
}

.op-desc {
  cursor: pointer;
}

.op-dash {
  color: #B4BEB8;
}

.op-chip {
  display: inline-flex;
  align-items: center;
  height: 25px;
  padding: 0 10px;
  border-radius: 8px;
  font-family: sofiaProRegular;
  font-size: 11.5px;
  line-height: 1;
  white-space: nowrap;
}

.op-chip .v-icon {
  margin-right: 5px;
}

.op-act {
  cursor: pointer;
}

/* ---------- empty + footer ---------- */
.op-empty {
  text-align: center;
  padding: 54px 16px 60px;
}

.op-empty__title {
  font-family: sofiaProMedium;
  font-size: 17px;
  color: #6B7C72;
  padding-top: 10px;
}

.op-empty__sub {
  font-family: sofiaProRegular;
  font-size: 13px;
  color: #9AA8A0;
  padding-top: 4px;
}

.op-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 16px 10px;
  border-top: 1px solid #F0F3F1;
}

.op-footer__txt {
  font-family: sofiaProRegular;
  font-size: 12.5px;
  color: #7A8B80;
}

.op-footer .v-pagination {
  width: auto;
}

.op-footer .v-pagination__item,
.op-footer .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid #EDF1EE;
}

/* ---------- kept for the dialogs on this page ---------- */
.heading {
  font-family: poppinsregular;
  font-size: 20px;
  font-weight: 900;
}

.subheading {
  font-family: poppinsregular;
  font-size: 15px;
  font-weight: 500;
}

.tableheading {
  font-family: poppinsregular;
  font-size: 15px !important;
  color: black !important;
  text-transform: uppercase !important;
}

.tablesubheading {
  font-family: poppinsregular;
  font-size: 10px;
}
</style>
